// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  BankAccountType,
  type BankAccount,
  type BankAccountOwner,
} from '@client/banks/types/account';
import {
  BankDebtAmortizationStatus,
  BankDebtProductType,
  type BankDebt,
  type BankDebtAmortization,
} from '@client/banks/types/debt';
import {
  BankPositionAssetType,
  type BankPosition,
} from '@client/banks/types/position';
import type { BankTransaction } from '@client/banks/types/transaction';
import { createDefaultTransactionDateTime } from '@client/banks/utils/transaction-datetime';
import dayjs from 'dayjs';

export const getDefaultBankCurrency = (
  currencyOptions: readonly { value: string }[],
): string => {
  if (currencyOptions.some(option => option.value === 'EUR')) {
    return 'EUR';
  }

  return currencyOptions[0]?.value ?? 'EUR';
};

export const createEmptyOwnerRow = (): BankAccountOwner => ({
  name: '',
});

export const createEmptyBankAccount = (currency: string): BankAccount => ({
  iban: '',
  name: '',
  currency,
  accountType: BankAccountType.Checking,
  available: null,
  balanceDate: null,
  owners: [],
});

export const createEmptyBankPosition = (
  currency: string,
  defaultIban = '',
): BankPosition => ({
  iban: defaultIban,
  isin: '',
  name: '',
  currency,
  assetType: BankPositionAssetType.Other,
  quantity: 0,
  price: 0,
  snapshotDate: dayjs().format('YYYY-MM-DD'),
});

export const createEmptyDebtAmortizationRow = (): BankDebtAmortization => ({
  installmentDate: '',
  status: BankDebtAmortizationStatus.Pending,
});

export const createEmptyBankDebt = (
  currency: string,
  defaultIban = '',
): BankDebt => ({
  iban: defaultIban,
  productType: BankDebtProductType.Loan,
  name: '',
  currency,
  pendingAmount: 0,
  snapshotDate: dayjs().format('YYYY-MM-DD'),
  amortizations: [],
});

export const createEmptyBankTransaction = (
  currency: string,
  defaultIban = '',
): BankTransaction => ({
  iban: defaultIban,
  transactionDate: createDefaultTransactionDateTime(),
  amount: 0,
  currency,
  description: '',
});
